import { ProgressBarColorCombination } from './progress-bar-color-combination.enum';
import { ProgressBarProps } from './progress-bar.interface';
import classNames from 'classnames';

/**
 * ProgressBar Component
 *
 * A styled `<progress>` bar that renders visually consistent progress bars across browsers.
 *
 * @param {ProgressBarProps} props - Props for the component.
 */
const LinearProgressBar: React.FC<ProgressBarProps> = ({
  value,
  range = 100,
  colorCombination = ProgressBarColorCombination.YellowForegroundWithGreyBackground,
}) => {
  // Ensure the value is within bounds (0 to range)
  const progressValue = Math.max(0, Math.min(value, range));
  const progressPercentage = (progressValue / range) * 100;

  const getClassesForCombination = (
    key: ProgressBarColorCombination
  ): { containerClasses: string[]; progressBarClasses: string[] } => {
    let classCombination: { containerClasses: string[]; progressBarClasses: string[] } = {
      containerClasses: [''],
      progressBarClasses: [''],
    };
    switch (key) {
      case ProgressBarColorCombination.YellowForegroundWithGreyBackground:
        classCombination = {
          containerClasses: ['bg-neutral-boldest-enabled', ''],
          progressBarClasses: [
            'bg-custom-yellow-default',
            'border-custom-reward-main',
            `${progressValue > 0 && 'border'}`,
          ],
        };
        break;

      case ProgressBarColorCombination.YellowForegroundWithGreenBackground:
        classCombination = {
          containerClasses: ['bg-brand-subtle-enabled', ''],
          progressBarClasses: [
            'bg-custom-yellow-default',
            'border-custom-reward-main',
            `${progressValue > 0 && 'border'}`,
          ],
        };
        break;

      case ProgressBarColorCombination.GreenForegroundWithGreyBackground:
        classCombination = {
          containerClasses: ['bg-neutral-boldest-enabled', ''],
          progressBarClasses: ['bg-level-bold', 'border-level-bold', `${progressValue > 0 && 'border'}`],
        };
        break;

      case ProgressBarColorCombination.GreenForegroundWithGreenBackground:
        classCombination = {
          containerClasses: ['bg-brand-subtle-enabled', ''],
          progressBarClasses: ['bg-level-bold', 'border-level-bold', `${value > 0 && 'border'}`],
        };
        break;

      default:
        classCombination = {
          containerClasses: ['', ''],
          progressBarClasses: ['', ''],
        };
        break;
    }
    return classCombination;
  };

  const getContainerBaseClasses = () => {
    return ['relative', 'w-full', 'h-2', 'rounded-md', 'transition-colors', 'duration-300'];
  };

  const ProgressContainerClasses = classNames(
    ...getContainerBaseClasses(),
    ...getClassesForCombination(colorCombination).containerClasses
  );

  const getProgressBarBaseClasses = () => {
    return ['h-2', 'transition-all', 'duration-300', 'ease-out', 'rounded-md', 'absolute', 'box-border'];
  };

  const ProgressBarClasses = classNames(
    ...getProgressBarBaseClasses(),
    ...getClassesForCombination(colorCombination).progressBarClasses
  );

  return (
    <div className={ProgressContainerClasses}>
      <div className={ProgressBarClasses} style={{ width: `${progressPercentage}%` }} />
    </div>
  );
};

export default LinearProgressBar;
